import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged, shareReplay } from 'rxjs/operators';

type KV = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class DataServiceService {
  // Single source of truth for all keyed values
  private readonly state$ = new BehaviorSubject<KV>({});

  /** Observe the whole store (use sparingly) */
  readonly currentData$: Observable<KV> = this.state$.asObservable();

  constructor() {}

  /** Set a key's value (emits to subscribers of that key) */
  set<T = unknown>(key: string, value: T): void {
    const curr = this.state$.getValue();
    // Avoid unnecessary emissions for identical references/values
    if (curr[key] === value) return;
    this.state$.next({ ...curr, [key]: value });
  }

  /** Get current value synchronously by key */
  getValue<T = unknown>(key: string): T | undefined {
    return this.state$.getValue()[key] as T | undefined;
  }

  /** Observe a single key's value */
  select<T = unknown>(key: string): Observable<T | undefined> {
    return this.state$.pipe(
      map(s => s[key] as T | undefined),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /** Remove a key */
  remove(key: string): void {
    const { [key]: _omit, ...rest } = this.state$.getValue();
    this.state$.next(rest);
  }

  /** Clear all keys */
  clear(): void {
    this.state$.next({});
  }
}
